import { Button, Select, TextInput } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useEffect, useState } from "react";

import { ERequestStatus } from "src/types/enum/request-status.enum";
import { ExecutionsSearchLimits } from "src/types/interfaces/executions-search-payload";
import ExecutionsSearchQs from "src/types/interfaces/executions-search-sq";
import { userProfileActions } from "src/store/reducers/user-reducer";
import { useOrganizationService } from "src/services/organization";
import { useUserService } from "src/services/user";
import { Organization } from "src/types/models/Organization";
import { User } from "src/types/models/User";

interface ExecutionsSearchProps {
    onSearch: (payload: ExecutionsSearchQs) => void;
}

function ExecutionsSearch({ onSearch }: ExecutionsSearchProps) {

    const userProfile = userProfileActions.useUserProfile();
    const organizationService = useOrganizationService();
    const userService = useUserService();

    const [doneDate, setDoneDate] = useState<Date | null>(null);
    const [executionRequestId, setExecutionRequestId] = useState<string>('');
    const [limit, setLimit] = useState<ExecutionsSearchLimits>(50);
    const [runningDate, setRunningDate] = useState<Date | null>(null);
    const [status, setStatus] = useState<ERequestStatus>(ERequestStatus.ALL);

    // Organization filter (admin only)
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

    // User filter (admin + org-manager)
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

    const showOrgFilter = userProfile.isAdmin;
    const showUserFilter = userProfile.isAdmin || userProfile.isOrganizationManager;

    // Load organizations for admin
    useEffect(() => {
        if (showOrgFilter) {
            organizationService.getOrganizations({
                success: (res: Organization[]) => setOrganizations(res),
                error: () => { /* no-op */ },
            });
        }
    }, [showOrgFilter]);

    // Load users for admin/org-manager, filtered by selected org for admin
    useEffect(() => {
        if (showUserFilter) {
            userService.getUsers({
                success: (res: User[]) => setUsers(res),
                error: () => { /* no-op */ },
            });
            // For admin with org filter, refetch users filtered by organization
            if (userProfile.isAdmin && selectedOrgId) {
                const fetchUsers = async () => {
                    try {
                        const token = localStorage.getItem('accessToken');
                        const response = await fetch(`${process.env.REACT_APP_API_URL}/users?organization=${selectedOrgId}`, {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                            },
                        });
                        const data = await response.json();
                        setUsers(data);
                    } catch {
                        // fallback: keep current users
                    }
                };
                fetchUsers();
            }
        }
    }, [showUserFilter, selectedOrgId]);

    const orgSelectData = organizations.map(org => ({
        value: org._id,
        label: org.name,
    }));

    const userSelectData = users.map(user => ({
        value: user._id,
        label: `${user.firstName} ${user.lastName} (${user.email})`,
    }));

    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const payload: ExecutionsSearchQs = {
            limit
        };
        if (doneDate) {
            payload.doneDate = doneDate?.getTime();
        }
        if (executionRequestId.trim().length > 0) {
            payload.executionRequestId = executionRequestId;
        }
        if (selectedOrgId) {
            payload.organization = selectedOrgId;
        }
        if (runningDate) {
            payload.runningDate = runningDate.getTime();
        }
        if (status != ERequestStatus.ALL) {
            payload.status = status;
        }
        if (selectedUserId) {
            payload.user = selectedUserId;
        }
        onSearch(payload);
    };

    return <>
        <form
            onSubmit={handleSearch}
            style={{
                marginBottom: '1rem',
                paddingLeft: '1rem',
                paddingTop: '1rem',
            }}
        >
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                marginBottom: '1rem',
                gap: '1rem',
            }}>
                <TextInput label="Execution id" value={executionRequestId} onChange={(event) => setExecutionRequestId(event.currentTarget.value)} />
                {showOrgFilter && (
                    <Select
                        searchable
                        clearable
                        label="Organization"
                        placeholder="All organizations"
                        data={orgSelectData}
                        value={selectedOrgId}
                        onChange={(value) => {
                            setSelectedOrgId(value);
                            setSelectedUserId(null);
                        }}
                    />
                )}
                {showUserFilter && (
                    <Select
                        searchable
                        clearable
                        label="User"
                        placeholder="All users"
                        data={userSelectData}
                        value={selectedUserId}
                        onChange={setSelectedUserId}
                    />
                )}
                <Select data={[ERequestStatus.ALL, ERequestStatus.NEW, ERequestStatus.RUNNING, ERequestStatus.DONE, ERequestStatus.ERROR, ERequestStatus.CANCELED]} label="Status" value={status} onChange={e => setStatus(e as ERequestStatus)} />
                <DatePickerInput label="Running date" value={runningDate} onChange={setRunningDate} />
                <DatePickerInput label="Done date" value={doneDate} onChange={setDoneDate} />
            </div>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                marginBottom: '1rem',
                gap: '1rem',
            }}>
                <Select data={["10", "50", "100", "200"]} label="Number of results" value={limit.toString()} onChange={e => {
                    if (!e) e = "50";
                    const limit = parseInt(e) as ExecutionsSearchLimits;
                    setLimit(limit);
                }} />
                <Button type="submit" style={{marginTop: 20}}>Search</Button>
            </div>
        </form>
    </>

}

export default ExecutionsSearch;
